import axios from 'axios';
import { refreshAdminSession } from './adminSessionRefresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
console.log('🔧 [adminAuthService.js] API_URL from env:', API_URL);
console.log('🔧 [adminAuthService.js] Login endpoint will be:', `${API_URL}/api/admin/auth/login`);

/**
 * Admin Authentication Service (Cookie-based)
 * Uses HttpOnly cookies for secure token storage
 * No localStorage usage - all data fetched from server via Context API
 */
class AdminAuthService {
  /**
   * Read CSRF token from cookie (double-submit pattern)
   */
  getCsrfTokenFromCookie() {
    const cookieName = 'csrf-token';
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === cookieName) {
        return decodeURIComponent(value);
      }
    }

    return null;
  }

  /**
   * Login admin
   * Token is set as HttpOnly cookie by backend
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{admin}>}
   */
  async login(email, password) {
    try {
      const response = await axios.post(`${API_URL}/api/admin/auth/login`, {
        email,
        password
      }, {
        withCredentials: true  // ⬅️ CRITICAL: Send/receive cookies
      });

      if (response.data.admin) {
        console.log('✅ Admin logged in via cookie:', response.data.admin.email);
      }

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async verifyMfa(code, newPassword) {
    try {
      const csrfToken = this.getCsrfTokenFromCookie();
      const response = await axios.post(`${API_URL}/api/admin/auth/mfa/verify`, {
        code,
        ...(newPassword !== undefined ? { newPassword } : {})
      }, {
        withCredentials: true,
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async refreshSession() {
    const response = await refreshAdminSession();
    return response.data;
  }

  async updateProfile(data) {
    try {
      const csrfToken = this.getCsrfTokenFromCookie();
      const response = await axios.patch(`${API_URL}/api/admin/auth/profile`, data, {
        withCredentials: true,
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Logout admin
   * Clears HttpOnly cookie on backend
   */
  async logout() {
    try {
      const csrfToken = this.getCsrfTokenFromCookie();
      await axios.post(`${API_URL}/api/admin/auth/logout`, {}, {
        withCredentials: true,  // ⬅️ Send cookies
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined
      });
      console.log('✅ Admin logged out');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  /**
   * Get current admin context from backend
   * Cookie is sent automatically
   * @returns {Promise<object>} Admin data
   */
  async getAdminContext() {
    try {
      const response = await axios.get(`${API_URL}/api/admin/auth/context`, {
        withCredentials: true  // ⬅️ Send cookies automatically
      });

      const { admin } = response.data;

      return admin;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if admin is authenticated
   * Try to fetch context from backend (cookie-based)
   */
  async isAuthenticated() {
    try {
      await this.getAdminContext();
      return true;
    } catch {
      return false;
    }
  }  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.error || 'Errore del server';
      const code = error.response.data?.code;

      return {
        message,
        code,
        status: error.response.status
      };
    } else if (error.request) {
      // Request made but no response
      return {
        message: 'Impossibile contattare il server',
        code: 'NETWORK_ERROR'
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'Errore sconosciuto',
        code: 'UNKNOWN_ERROR'
      };
    }
  }
}

const adminAuthService = new AdminAuthService();
export default adminAuthService;
